import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Edit,
  Shield
} from 'lucide-react';

const Profile = () => {
  const { user } = useAuth();

  const getRoleLabel = (role) => {
    switch (role) {
      case 'patient': return 'Patient';
      case 'professional': return 'Professionnel';
      case 'agent': return 'Agent terrain';
      case 'admin': return 'Administrateur';
      default: return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'patient': return 'bg-blue-100 text-blue-700';
      case 'professional': return 'bg-red-100 text-red-600';
      case 'agent': return 'bg-amber-100 text-amber-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="section-padding py-6" data-testid="profile-page">
      <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
        Mon profil
      </h1>

      <Card className="border-0 shadow-lg mb-6 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-[#E53935] via-[#2196F3] to-[#1A237E]" />
        <CardContent className="relative -mt-12 pb-6">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
              <AvatarImage src={user?.profile_image} />
              <AvatarFallback className="bg-red-100 text-red-600 text-3xl">
                {user?.full_name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900">{user?.full_name}</h2>
              <Badge className={`mt-2 ${getRoleColor(user?.role)}`}>
                {getRoleLabel(user?.role)}
              </Badge>
            </div>
            <Link to="/edit-profile">
              <Button className="rounded-full gap-2 bg-[#1A237E] hover:bg-[#3949AB]">
                <Edit className="w-4 h-4" />
                Modifier mon profil
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-red-500" />
              Informations personnelles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Téléphone</p>
                <p className="font-medium text-slate-900">{user?.phone}</p>
              </div>
            </div>
            {user?.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">{user?.email}</p>
                </div>
              </div>
            )}
            {user?.location && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Localisation</p>
                  <p className="font-medium text-slate-900">{user?.location}</p>
                </div>
              </div>
            )}
            {user?.age && (
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Âge</p>
                  <p className="font-medium text-slate-900">{user?.age} ans</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              Statut du compte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <span className="text-slate-600">Compte vérifié</span>
              <Badge className={user?.is_verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                {user?.is_verified ? 'Oui' : 'En attente'}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <span className="text-slate-600">Type de compte</span>
              <Badge className={getRoleColor(user?.role)}>
                {getRoleLabel(user?.role)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
